import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useHttp } from 'hooks/http.hook';
import { EMPLOYEE, EMPLOYEE_V2 } from 'endpointsConst';
import { ITEMS_PER_PAGE } from 'constants/settings';
import EmployeeRow from './EmployeeRow';
import styles from './styles.module.scss';

const SEARCH_DEBOUNCE_DELAY = 350;

const EmployeesSection = ({ employees: propEmployees, companyId }) => {
  const { t } = useTranslation();
  const { request, loading } = useHttp();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [employees, setEmployees] = useState(propEmployees || []);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const fetchEmployees = useCallback((query = '') => {
    if (companyId && !propEmployees) {
      const endpoint = query.trim()
          ? `${EMPLOYEE}/search`
          : `${EMPLOYEE_V2}/${companyId}`;

      const params = query.trim()
          ? { q: query.trim(), offset, limit: ITEMS_PER_PAGE }
          : { offset, limit: ITEMS_PER_PAGE };

      request(endpoint, 'get', { params })
          .then((result) => {
            setEmployees(result.data.items || result.data || []);
            setTotalCount(result.data.count || 0);
          })
          .catch((e) => {
            console.error('Error fetching employees:', e);
            setEmployees([]);
            setTotalCount(0);
          });
    }
  }, [companyId, request, propEmployees, offset]);

  useEffect(() => {
    fetchEmployees(debouncedQuery);
  }, [fetchEmployees, debouncedQuery]);

  useEffect(() => {
    if (propEmployees) {
      setEmployees(propEmployees);
    }
  }, [propEmployees]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedQuery(value);
      setOffset(0);
    }, SEARCH_DEBOUNCE_DELAY);
  };

  const pagesCount = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const currentPage = Math.floor(offset / ITEMS_PER_PAGE);
  const hasPagination = pagesCount > 1;

  const handlePageChange = (page) => {
    const newOffset = page * ITEMS_PER_PAGE;
    setOffset(newOffset);
  };

  const startItem = offset + 1;
  const endItem = Math.min(offset + ITEMS_PER_PAGE, totalCount);

  return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            {t('employee.allEmployees', { count: totalCount })} ({totalCount})
          </h3>
          <div className={styles.searchWrapper}>
            <input
                type="text"
                className={styles.searchInput}
                placeholder={t('employee.searchPlaceholder')}
                value={searchQuery}
                onChange={handleSearchChange}
            />
            <span className={styles.searchIcon}/>
          </div>
        </div>

        <div className={styles.table}>
          <div className={styles.tableHeader}>
            <div className={styles.headerCell} style={{ width: '40%' }}>
              <span>{t('employee.nameAndSurname') + ' /'}</span>
              <span>{t('employee.position')}</span>
            </div>
            <div className={styles.headerCell} style={{ width: '30%' }}>
              <span>{t('employee.contact')}</span>
            </div>
            <div className={styles.headerCell} style={{ width: '30%' }}>
              {/* <span>{t('employee.lastLogin') + ' /'}</span> */}
              <span>{t('employee.registrationDate')}</span>
            </div>
          </div>

          <div className={styles.tableBody}>
            {loading ? (
                <div className={styles.loadingState}>
                  <span className={styles.spinner}/>
                </div>
            ) : employees.length > 0 ? (
                employees.map((employee) => (
                    <EmployeeRow
                        key={employee.id}
                        employee={employee}
                    />
                ))
            ) : (
                <div className={styles.emptyState}>
                  {searchQuery ? t('employee.noResults') : t('employee.noEmployees')}
                </div>
            )}
          </div>
        </div>

        {hasPagination && (
            <div className={styles.pagination}>
              <div className={styles.paginationInfo}>
                {startItem} - {endItem} из {totalCount}
              </div>
              <div className={styles.paginationControls}>
                <button
                    type="button"
                    className={styles.paginationButton}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 0}
                >
                  &lt;
                </button>
                {Array.from({ length: pagesCount }, (_, i) => (
                    <button
                        type="button"
                        key={i}
                        className={`${styles.paginationButton} ${currentPage === i ? styles.paginationButtonActive : ''}`}
                        onClick={() => handlePageChange(i)}
                    >
                      {i + 1}
                    </button>
                ))}
                <button
                    type="button"
                    className={styles.paginationButton}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagesCount - 1}
                >
                  &gt;
                </button>
              </div>
            </div>
        )}
      </div>
  );
};

export default EmployeesSection;
